import {
  TextInput,
  Textarea,
  Stack,
  Switch,
  Select,
  ColorInput,
  Group,
  Box,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { DiagnosisCategory } from "../../../services/admin/diagnosiscategories.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface DiagnosisCategoryFormValues {
  name: string;
  description: string;
  markerShape: string;
  markerColor: string;
  isActive: boolean;
}

interface DiagnosisCategoryFormProps {
  initialValues?: DiagnosisCategory;
  onSubmit: (values: DiagnosisCategoryFormValues) => void;
}

const MARKER_SHAPE_OPTIONS = [
  { value: "circle", label: "Circle" },
  { value: "square", label: "Square" },
  { value: "triangle", label: "Triangle" },
  { value: "diamond", label: "Diamond" },
  { value: "star", label: "Star" },
];

function MarkerPreview({ shape, color }: { shape: string; color: string }) {
  if (!shape && !color) return null;

  const previewColor = color || "#868e96";
  const size = 24;

  const shapeElement = () => {
    switch (shape) {
      case "circle":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill={previewColor} />
          </svg>
        );
      case "square":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <rect x="2" y="2" width="20" height="20" fill={previewColor} />
          </svg>
        );
      case "triangle":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon points="12,2 22,22 2,22" fill={previewColor} />
          </svg>
        );
      case "diamond":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon points="12,2 22,12 12,22 2,12" fill={previewColor} />
          </svg>
        );
      case "star":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon
              points="12,2 15,9 22,9 16.5,14 18.5,22 12,17.5 5.5,22 7.5,14 2,9 9,9"
              fill={previewColor}
            />
          </svg>
        );
      default:
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill={previewColor} />
          </svg>
        );
    }
  };

  return (
    <Group gap="sm" align="center">
      {shapeElement()}
      <Text size="xs" c="dimmed">
        Organ map marker preview
      </Text>
    </Group>
  );
}

export default function DiagnosisCategoryForm({
  initialValues,
  onSubmit,
}: DiagnosisCategoryFormProps) {
  const form = useForm<DiagnosisCategoryFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      markerShape: initialValues?.markerShape || "",
      markerColor: initialValues?.markerColor || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      name: required("Category name is required"),
    },
  });

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="Category Name"
          placeholder="e.g., Malignant, Benign, Dysplasia"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          placeholder="Enter category description"
          minRows={3}
          {...form.getInputProps("description")}
        />

        <Box>
          <Text size="sm" fw={500} mb="xs">
            Organ Map Marker
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Configure how this category appears on organ map reports
          </Text>

          <Group grow align="flex-start">
            <Select
              label="Marker Shape"
              placeholder="Select shape"
              data={MARKER_SHAPE_OPTIONS}
              clearable
              {...form.getInputProps("markerShape")}
            />

            <ColorInput
              label="Marker Color"
              placeholder="Select color"
              format="hex"
              swatches={[
                "#25262b",
                "#868e96",
                "#fa5252",
                "#e64980",
                "#be4bdb",
                "#7950f2",
                "#4c6ef5",
                "#228be6",
                "#15aabf",
                "#12b886",
                "#40c057",
                "#82c91e",
                "#fab005",
                "#fd7e14",
              ]}
              {...form.getInputProps("markerColor")}
            />
          </Group>

          {(form.values.markerShape || form.values.markerColor) && (
            <Box mt="sm">
              <MarkerPreview
                shape={form.values.markerShape}
                color={form.values.markerColor}
              />
            </Box>
          )}
        </Box>

        <Switch
          label="Active"
          description="Inactive categories won't appear in diagnosis selection"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
